import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../config/supabaseClient';
import { toast } from 'react-hot-toast';
import { useParams, useNavigate } from 'react-router-dom';

interface Contact {
  id: string;
  email: string;
  first_name: string | null;
  last_name: string | null;
  company: string | null;
  position: string | null;
  status: 'active' | 'unsubscribed' | 'bounced';
}

interface CampaignContact {
  id: string;
  campaign_id: string;
  contact_id: string;
  status: 'pending' | 'sent' | 'opened' | 'replied' | 'bounced' | 'failed';
  sent_at: string | null;
  opened_at: string | null;
  replied_at: string | null;
  bounced_at: string | null;
  contact: Contact;
}

interface Campaign {
  id: string;
  name: string;
  subject: string;
  status: 'draft' | 'active' | 'paused' | 'completed';
  total_contacts: number;
  sent_count: number;
  opened_count: number;
  replied_count: number;
  bounced_count: number;
}

const CampaignContacts: React.FC = () => {
  const { campaignId } = useParams<{ campaignId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [campaign, setCampaign] = useState<Campaign | null>(null);
  const [campaignContacts, setCampaignContacts] = useState<CampaignContact[]>([]);
  const [availableContacts, setAvailableContacts] = useState<Contact[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedContacts, setSelectedContacts] = useState<string[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [isAdding, setIsAdding] = useState(false);

  useEffect(() => {
    if (user && campaignId) {
      fetchCampaignData();
      fetchCampaignContacts();
    }
  }, [user, campaignId]);

  const fetchCampaignData = async () => {
    try {
      const { data, error } = await supabase
        .from('email_campaigns')
        .select('*')
        .eq('id', campaignId)
        .single();

      if (error) throw error;
      setCampaign(data);
    } catch (error) {
      console.error('Error fetching campaign:', error);
      toast.error('Failed to load campaign data');
      navigate('/email/campaigns');
    }
  };

  const fetchCampaignContacts = async () => {
    try {
      const { data, error } = await supabase
        .from('campaign_contacts')
        .select(`
          *,
          contact:email_contacts(*)
        `)
        .eq('campaign_id', campaignId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setCampaignContacts(data || []);
    } catch (error) {
      console.error('Error fetching campaign contacts:', error);
      toast.error('Failed to load campaign contacts');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchAvailableContacts = async () => {
    try {
      // Get contacts that are not already in this campaign
      const { data: existingContactIds, error: existingError } = await supabase
        .from('campaign_contacts')
        .select('contact_id')
        .eq('campaign_id', campaignId);

      if (existingError) throw existingError;

      const existingIds = existingContactIds?.map(cc => cc.contact_id) || [];

      const { data, error } = await supabase
        .from('email_contacts')
        .select('*')
        .eq('status', 'active')
        .not('id', 'in', `(${existingIds.join(',')})`)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setAvailableContacts(data || []);
    } catch (error) {
      console.error('Error fetching available contacts:', error);
      toast.error('Failed to load available contacts');
    }
  };

  const handleAddContacts = async () => {
    if (selectedContacts.length === 0) {
      toast.error('Please select at least one contact');
      return;
    }

    setIsAdding(true);
    try {
      const contactsToAdd = selectedContacts.map(contactId => ({
        campaign_id: campaignId,
        contact_id: contactId,
        status: 'pending',
        user_id: user?.id
      }));

      const { error } = await supabase
        .from('campaign_contacts')
        .insert(contactsToAdd);

      if (error) throw error;

      // Update campaign total_contacts count
      const { error: updateError } = await supabase
        .from('email_campaigns')
        .update({ 
          total_contacts: (campaign?.total_contacts || 0) + selectedContacts.length 
        })
        .eq('id', campaignId);

      if (updateError) throw updateError;

      toast.success(`Added ${selectedContacts.length} contacts to campaign`);
      setShowAddModal(false);
      setSelectedContacts([]);
      fetchCampaignContacts();
      fetchCampaignData();
    } catch (error) {
      console.error('Error adding contacts:', error);
      toast.error('Failed to add contacts to campaign');
    } finally {
      setIsAdding(false);
    }
  };

  const handleRemoveContact = async (contactId: string) => {
    if (!window.confirm('Are you sure you want to remove this contact from the campaign?')) return;

    try {
      const { error } = await supabase
        .from('campaign_contacts')
        .delete()
        .eq('campaign_id', campaignId)
        .eq('contact_id', contactId);

      if (error) throw error;

      // Update campaign total_contacts count
      const { error: updateError } = await supabase
        .from('email_campaigns')
        .update({ 
          total_contacts: Math.max((campaign?.total_contacts || 1) - 1, 0)
        })
        .eq('id', campaignId);

      if (updateError) throw updateError;

      setCampaignContacts(prev => prev.filter(cc => cc.contact_id !== contactId));
      toast.success('Contact removed from campaign');
      fetchCampaignData();
    } catch (error) {
      console.error('Error removing contact:', error);
      toast.error('Failed to remove contact from campaign');
    }
  };

  const handleBulkRemove = async () => {
    if (selectedContacts.length === 0) return;
    
    if (!window.confirm(`Are you sure you want to remove ${selectedContacts.length} contacts from the campaign?`)) return;

    try {
      const { error } = await supabase
        .from('campaign_contacts')
        .delete()
        .eq('campaign_id', campaignId)
        .in('contact_id', selectedContacts);

      if (error) throw error;

      // Update campaign total_contacts count
      const { error: updateError } = await supabase
        .from('email_campaigns')
        .update({ 
          total_contacts: Math.max((campaign?.total_contacts || selectedContacts.length) - selectedContacts.length, 0)
        })
        .eq('id', campaignId);

      if (updateError) throw updateError;

      setCampaignContacts(prev => prev.filter(cc => !selectedContacts.includes(cc.contact_id)));
      setSelectedContacts([]);
      toast.success(`Removed ${selectedContacts.length} contacts from campaign`);
      fetchCampaignData();
    } catch (error) {
      console.error('Error removing contacts:', error);
      toast.error('Failed to remove contacts from campaign');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'bg-gray-100 text-gray-800';
      case 'sent':
        return 'bg-blue-100 text-blue-800';
      case 'opened':
        return 'bg-green-100 text-green-800';
      case 'replied':
        return 'bg-purple-100 text-purple-800';
      case 'bounced':
        return 'bg-red-100 text-red-800';
      case 'failed':
        return 'bg-orange-100 text-orange-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const filteredCampaignContacts = campaignContacts.filter(cc => {
    const matchesSearch = cc.contact.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         cc.contact.first_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         cc.contact.last_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         cc.contact.company?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = filterStatus === 'all' || cc.status === filterStatus;
    
    return matchesSearch && matchesStatus;
  });

  const filteredAvailableContacts = availableContacts.filter(contact =>
    contact.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    contact.first_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    contact.last_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    contact.company?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleSelectContact = (contactId: string, checked: boolean) => {
    if (checked) {
      setSelectedContacts(prev => [...prev, contactId]);
    } else {
      setSelectedContacts(prev => prev.filter(id => id !== contactId));
    }
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedContacts(filteredCampaignContacts.map(cc => cc.contact_id));
    } else {
      setSelectedContacts([]);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-2 text-gray-600">Loading campaign contacts...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold">{campaign?.name} - Contacts</h1>
          <p className="text-gray-600 mt-1">{campaign?.subject}</p>
        </div>
        <div className="space-x-2">
          <button
            onClick={() => navigate('/email/campaigns')}
            className="bg-gray-600 hover:bg-gray-700 text-white font-semibold py-2 px-4 rounded"
          >
            Back to Campaigns
          </button>
          <button
            onClick={() => {
              setShowAddModal(true);
              fetchAvailableContacts();
            }}
            className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-4 rounded"
          >
            Add Contacts
          </button>
        </div>
      </div>

      {/* Campaign Summary */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <div className="text-center">
            <p className="text-sm text-gray-500">Total Contacts</p>
            <p className="text-2xl font-bold text-gray-900">{campaign?.total_contacts || 0}</p>
          </div>
          <div className="text-center">
            <p className="text-sm text-gray-500">Sent</p>
            <p className="text-2xl font-bold text-blue-600">{campaign?.sent_count || 0}</p>
          </div>
          <div className="text-center">
            <p className="text-sm text-gray-500">Opened</p>
            <p className="text-2xl font-bold text-green-600">{campaign?.opened_count || 0}</p>
          </div>
          <div className="text-center">
            <p className="text-sm text-gray-500">Replied</p>
            <p className="text-2xl font-bold text-purple-600">{campaign?.replied_count || 0}</p>
          </div>
          <div className="text-center">
            <p className="text-sm text-gray-500">Bounced</p>
            <p className="text-2xl font-bold text-red-600">{campaign?.bounced_count || 0}</p>
          </div>
        </div>
      </div>

      {/* Search and Filter */}
      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-4 md:space-y-0">
          <div className="flex items-center space-x-4">
            <input
              type="text"
              placeholder="Search contacts..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">All Statuses</option>
              <option value="pending">Pending</option>
              <option value="sent">Sent</option>
              <option value="opened">Opened</option>
              <option value="replied">Replied</option>
              <option value="bounced">Bounced</option>
              <option value="failed">Failed</option>
            </select>
            <span className="text-gray-600">
              {filteredCampaignContacts.length} of {campaignContacts.length} contacts
            </span>
          </div>
          {selectedContacts.length > 0 && (
            <div className="flex items-center space-x-2">
              <span className="text-sm text-gray-600">
                {selectedContacts.length} selected
              </span>
              <button
                onClick={handleBulkRemove}
                className="bg-red-600 hover:bg-red-700 text-white px-3 py-1 rounded text-sm"
              >
                Remove Selected
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Contacts Table */}
      {filteredCampaignContacts.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-8 text-center">
          <div className="text-6xl mb-4">👥</div>
          <h2 className="text-2xl font-semibold mb-2">No Contacts in Campaign</h2>
          <p className="text-gray-600 mb-6">
            {searchTerm || filterStatus !== 'all' 
              ? 'No contacts match your search criteria.' 
              : 'You haven\'t added any contacts to this campaign yet.'
            }
          </p>
          {!searchTerm && filterStatus === 'all' && (
            <button
              onClick={() => {
                setShowAddModal(true);
                fetchAvailableContacts();
              }}
              className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-4 rounded"
            >
              Add Contacts
            </button>
          )}
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-6 py-3 text-left">
                  <input
                    type="checkbox"
                    checked={selectedContacts.length === filteredCampaignContacts.length && filteredCampaignContacts.length > 0}
                    onChange={(e) => handleSelectAll(e.target.checked)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Contact
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Company
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Activity
                </th>
                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredCampaignContacts.map((campaignContact) => (
                <tr key={campaignContact.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <input
                      type="checkbox"
                      checked={selectedContacts.includes(campaignContact.contact_id)}
                      onChange={(e) => handleSelectContact(campaignContact.contact_id, e.target.checked)}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-10 w-10">
                        <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                          <span className="text-blue-600 font-medium">
                            {campaignContact.contact.first_name 
                              ? campaignContact.contact.first_name[0] 
                              : campaignContact.contact.email[0].toUpperCase()
                            }
                          </span>
                        </div>
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">
                          {campaignContact.contact.first_name && campaignContact.contact.last_name 
                            ? `${campaignContact.contact.first_name} ${campaignContact.contact.last_name}`
                            : campaignContact.contact.email
                          }
                        </div>
                        <div className="text-sm text-gray-500">{campaignContact.contact.email}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    <div>{campaignContact.contact.company || '-'}</div>
                    <div className="text-sm text-gray-500">{campaignContact.contact.position || ''}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(campaignContact.status)}`}>
                      {campaignContact.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    <div className="space-y-1">
                      {campaignContact.sent_at && (
                        <div>Sent: {new Date(campaignContact.sent_at).toLocaleDateString()}</div>
                      )}
                      {campaignContact.opened_at && (
                        <div>Opened: {new Date(campaignContact.opened_at).toLocaleDateString()}</div>
                      )}
                      {campaignContact.replied_at && (
                        <div>Replied: {new Date(campaignContact.replied_at).toLocaleDateString()}</div>
                      )}
                      {campaignContact.bounced_at && (
                        <div>Bounced: {new Date(campaignContact.bounced_at).toLocaleDateString()}</div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <button
                      onClick={() => handleRemoveContact(campaignContact.contact_id)}
                      className="text-red-600 hover:text-red-900"
                    >
                      Remove
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Add Contacts Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-screen overflow-y-auto">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">Add Contacts to Campaign</h3>
                <button 
                  onClick={() => setShowAddModal(false)}
                  className="text-gray-400 hover:text-gray-500"
                >
                  &times;
                </button>
              </div>
            </div>
            
            <div className="px-6 py-4">
              <input
                type="text"
                placeholder="Search available contacts..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 mb-4"
              />
              
              {filteredAvailableContacts.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-gray-500">No available contacts to add.</p>
                </div>
              ) : (
                <div className="max-h-96 overflow-y-auto">
                  <div className="space-y-2">
                    {filteredAvailableContacts.map((contact) => (
                      <div key={contact.id} className="flex items-center p-3 border rounded-lg hover:bg-gray-50">
                        <input
                          type="checkbox"
                          checked={selectedContacts.includes(contact.id)}
                          onChange={(e) => handleSelectContact(contact.id, e.target.checked)}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded mr-3"
                        />
                        <div className="flex-1">
                          <div className="text-sm font-medium text-gray-900">
                            {contact.first_name && contact.last_name 
                              ? `${contact.first_name} ${contact.last_name}`
                              : contact.email
                            }
                          </div>
                          <div className="text-sm text-gray-500">{contact.email}</div>
                          {contact.company && (
                            <div className="text-sm text-gray-500">{contact.company}</div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            
            <div className="px-6 py-4 bg-gray-50 flex justify-between">
              <div className="text-sm text-gray-600">
                {selectedContacts.length} contact{selectedContacts.length !== 1 ? 's' : ''} selected
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => setShowAddModal(false)}
                  className="px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleAddContacts}
                  disabled={selectedContacts.length === 0 || isAdding}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-md disabled:opacity-50"
                >
                  {isAdding ? 'Adding...' : `Add ${selectedContacts.length} Contact${selectedContacts.length !== 1 ? 's' : ''}`}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CampaignContacts;
